import { useContext, useEffect, useRef, useState } from "react";
import { Button } from "../../../../stories/Button/Button";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../common/layout/Navbar";
import { LicenseTypes, Operation, empType } from "../../../../utils/Enum.types";
import { Title } from "../../../../stories/Title/Title";
import Input from "../../../../stories/Input/Input";
import List from "./List";
import {
  AddCaseStudyStyles,
  AddModalStyles,
} from "../../../../styles/CustomModalStyles";
import { CustomModal } from "../../../../utils/UtilFunctions";
import Add from "./Add";
import { useLazyQuery } from "@apollo/client";
import { GetCaseStudies } from "./queries/query";
import {
  CaseStudiesEdge,
  CaseStudiesOrderField,
  GetCaseStudiesData,
  GetCaseStudiesVar,
} from "./queries/types";
import useUrlParams from "../../../../customHooks/useUrlParams";
import { OrderDirection } from "../../../../utils/commonQueryTypes";
import useLoggedInUserDetails from "../../../../customHooks/useLoggedInUserDetails";
import { useHotKey } from "../../../../utils/hotKeys";
import { SpecialKeys } from "../../../../utils/Enum.keys";
import { AppContext } from "../../../../context/context";
import { EMPTY_STRING, FETCH_MORE_DATA } from "../../../../utils/constants";
import CaseTypeAutocomplete from "./common/CaseTypeAutocomplete";

const CaseStudy = () => {
  const navigate = useNavigate();

  const { customer_id } = useUrlParams();
  const { state } = useContext(AppContext);

  const { user_details } = useLoggedInUserDetails();

  const [addCaseStudy, setAddCaseStudy] = useState(false);

  const [searchData, setSearchData] = useState("");
  const [caseTypeSelected, setSelectedCaseType] = useState<string>("");
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [employees, setEmployees] = useState<CaseStudiesEdge[]>([]);

  const lastElementRef = useRef<HTMLDivElement | null>(null);

  const [GetCaseStudyList, { data, loading, fetchMore }] = useLazyQuery<
    GetCaseStudiesData,
    GetCaseStudiesVar
  >(GetCaseStudies);

  useEffect(() => {
    if (customer_id)
      GetCaseStudyList({
        variables: {
          after: null,
          first: 50,
          orderBy: {
            direction: OrderDirection.ASC,
            field: CaseStudiesOrderField.CREATED_AT,
          },
          caseType: caseTypeSelected ? caseTypeSelected :EMPTY_STRING,
          name: searchData,
          customerID: customer_id,
          empID: user_details.id,
        },
      });
  }, [customer_id, GetCaseStudyList, searchData, user_details.id ,caseTypeSelected]);

  useHotKey({
    handleFunc: () => {
      setAddCaseStudy(!addCaseStudy);
    },
    specialCharacter: SpecialKeys.CTRL,
    charater: "a",
  });

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetCaseStudies.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newEmp) => {
          const filteredStudent = employees.find(
            (employee) => employee.node.id === newEmp.node.id
          );
          if (filteredStudent) {
            return {
              ...newEmp,
              node: {
                ...newEmp.node,
              },
            };
          }
          return newEmp;
        });
        setEmployees(updatedNewData);
      } else {
        setEmployees(newData);
      }
      setEndCursor(data.GetCaseStudies.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  const handlescroll = () => {
    if (hasNextPage && !loading) {
      fetchMore({
        variables: {
          first: FETCH_MORE_DATA,
          after: endCursor,
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prevResult;

          const newEdges = fetchMoreResult.GetCaseStudies.edges;
          const pageInfo = fetchMoreResult.GetCaseStudies.pageInfo;
          setEndCursor(pageInfo.endCursor);
          setHasNextPage(pageInfo.hasNextPage);

          const duplicateCheck = prevResult.GetCaseStudies.edges.filter(
            ({ node: { id } }) =>
              newEdges.findIndex(({ node: { id: newId } }) => newId === id) !==
              -1
          );

          if (duplicateCheck.length > 0) return prevResult;

          return {
            GetCaseStudies: {
              edges: [...employees, ...newEdges],
              pageInfo,
              totalCount: data ? data.GetCaseStudies.totalCount! : 0,
            },
          };
        },
      });
    }
  };

  // Observe the last element
  useEffect(() => {
    if (loading) return;

    const observerInstance = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          handlescroll();
        }
      },
      {
        threshold: 1.0,
      }
    );

    const currentRef = lastElementRef.current;

    if (currentRef) {
      observerInstance.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observerInstance.unobserve(currentRef);
      }
    };
  }, [employees, hasNextPage, loading]);
  

  return (
    <>
      <Navbar NavType={LicenseTypes.DASHBOARD} />
      <Title>PMU-Traffic Engineering Studies</Title>

      <div className="case-study">
        <div className="case-study__row row g-0">
          <div className="col-2">
            <Input
              id="data_search"
              placeholder="Search..."
              onChange={(e) => {
                setSearchData(e.target.value);
              }}
            />
          </div>
          <div className="col-2">
            <CaseTypeAutocomplete
              name="caseType"
              value={caseTypeSelected}
              clearFunction={() => setSelectedCaseType("")}
              onChange={(newValue) => {
                setSelectedCaseType(newValue);
              }}
              label="Filter By Case Type"
            />
          </div>
          <div className="col"></div>
          {state.empSubType === empType.PMU && (
            <div className="col-2 flex-end">
              <Button
                mode="addnew"
                onClick={() => setAddCaseStudy(!addCaseStudy)}
                autoFocus
              />
            </div>
          )}
        </div>
        <div className="case-study__block">
          {employees
            ? employees.map((res, index) => {
                return <List res={res.node} index={index} />;
              })
            : null}
          <div ref={lastElementRef} style={{ height: "1px" }}></div>
        </div>
        <div className="row">
          <div className="col-2">
            <Button mode="back" onClick={() => navigate(-1)} />
          </div>
          <div className="col"></div>
          <div className="col-2 flex-end">
            <div className="overall-total-count">
              Total Count <b>{data?.GetCaseStudies.totalCount}</b>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        style={AddCaseStudyStyles}
        isOpen={addCaseStudy}
        onRequestClose={() => setAddCaseStudy(!addCaseStudy)}
        contentComponent={
          <Add
            setModal={setAddCaseStudy}
            caseStudyID={0}
            operation={Operation.CREATE}
          />
        }
      />
    </>
  );
};

export default CaseStudy;
